import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Play, Pause, ChevronLeft, ChevronRight, RotateCcw, Code2, ExternalLink
} from 'lucide-react';
import SequenceDiagram from '../components/SequenceDiagram';
import { FSPIOP_SCENARIOS, FSPIOP_ACTORS, FSPIOP_SCENARIO_ORDER, GUIDE_LINKS } from '../constants/fspiopFlowScenarios';

const KIND_BADGE = {
  req:  'bg-blue-100 text-blue-800',
  ok:   'bg-green-100 text-green-800',
  warn: 'bg-amber-100 text-amber-800',
  rjct: 'bg-red-100 text-red-800',
};

const STEP_MS = 1700;

export default function FspiopFlowSimulator() {
  const [scenarioId, setScenarioId] = useState(FSPIOP_SCENARIO_ORDER[0]);
  const [stepIndex, setStepIndex]   = useState(0);
  const [playing, setPlaying]       = useState(false);
  const [showBody, setShowBody]     = useState(false);
  const timerRef = useRef(null);

  const scenario = FSPIOP_SCENARIOS[scenarioId];
  const step = scenario.steps[stepIndex];

  useEffect(() => {
    if (!playing) return;
    timerRef.current = setInterval(() => {
      setStepIndex(prev => {
        if (prev >= scenario.steps.length - 1) { setPlaying(false); return prev; }
        return prev + 1;
      });
    }, STEP_MS);
    return () => clearInterval(timerRef.current);
  }, [playing, scenario.steps.length]);

  const switchScenario = (id) => { setScenarioId(id); setStepIndex(0); setPlaying(false); setShowBody(false); };
  const goTo = (i) => { setPlaying(false); setStepIndex(Math.max(0, Math.min(i, scenario.steps.length - 1))); };
  const togglePlay = () => {
    if (!playing && stepIndex >= scenario.steps.length - 1) setStepIndex(0);
    setPlaying(p => !p);
  };

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">FSPIOP Flow Simulator</h1>
        <p className="text-sm text-gray-500 mt-1">
          The three-phase FSPIOP choreography — party lookup → quote → transfer. Each step shows
          where JWS signing, async correlation, and ILP verification apply, linking back to the
          relevant guide.
        </p>
      </div>

      {/* Scenario picker */}
      <div className="flex flex-wrap gap-2">
        {FSPIOP_SCENARIO_ORDER.map((id, i) => (
          <button key={id} onClick={() => switchScenario(id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
              id === scenarioId ? 'bg-primary-600 text-white border-primary-600'
                                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}>
            <span className="text-xs opacity-60 mr-1.5">{i + 1}</span>{FSPIOP_SCENARIOS[id].label}
          </button>
        ))}
      </div>

      {/* Diagram */}
      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
        <SequenceDiagram actors={FSPIOP_ACTORS} steps={scenario.steps} activeIndex={stepIndex} onStepClick={goTo} />
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3">
        <button onClick={() => goTo(stepIndex - 1)} disabled={stepIndex === 0}
          className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button onClick={togglePlay}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700">
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          {playing ? 'Pause' : 'Play phase'}
        </button>
        <button onClick={() => goTo(stepIndex + 1)} disabled={stepIndex === scenario.steps.length - 1}
          className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40">
          <ChevronRight className="w-5 h-5" />
        </button>
        <button onClick={() => goTo(0)} className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50" aria-label="Reset">
          <RotateCcw className="w-5 h-5" />
        </button>
        <span className="text-sm text-gray-500 min-w-[44px] text-right">{stepIndex + 1} / {scenario.steps.length}</span>
      </div>

      {/* Detail panel */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center gap-3 mb-2 flex-wrap">
          <span className={`font-mono text-sm px-2.5 py-1 rounded-md ${KIND_BADGE[step.kind] || KIND_BADGE.req}`}>
            {step.msgType}
          </span>
          <span className="text-sm text-gray-500">{FSPIOP_ACTORS[step.from]} → {FSPIOP_ACTORS[step.to]}</span>
        </div>
        <p className="text-base font-semibold text-gray-900 mb-1">{step.title}</p>
        <p className="text-sm text-gray-600 leading-relaxed mb-3">{step.description}</p>

        {/* applies badges → guide links */}
        {step.applies?.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-xs text-gray-400">applies:</span>
            {step.applies.map(tag => {
              const g = GUIDE_LINKS[tag];
              return (
                <Link key={tag} to={g.href}
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-primary-50 text-primary-700 hover:bg-primary-100">
                  {g.label} <ExternalLink className="w-3 h-3" />
                </Link>
              );
            })}
          </div>
        )}

        {/* sample body */}
        {step.body && (
          <>
            <button onClick={() => setShowBody(s => !s)}
              className="mt-3 flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700">
              <Code2 className="w-4 h-4" /> {showBody ? 'Hide' : 'View'} sample message
            </button>
            {showBody && (
              <pre className="mt-2 bg-gray-900 text-gray-100 text-xs rounded-lg p-4 overflow-x-auto whitespace-pre-wrap">
                {step.body}
              </pre>
            )}
          </>
        )}
      </div>
    </div>
  );
}
